import React, { createContext, useContext, useRef, useState, ReactNode } from 'react';

interface AudioContextType {
  currentAudio: string | null;
  isPlaying: boolean;
  play: (audioSrc: string) => void;
  pause: () => void;
  stop: () => void;
}

const AudioContext = createContext<AudioContextType | undefined>(undefined);

interface AudioProviderProps {
  children: ReactNode;
}

export const AudioProvider: React.FC<AudioProviderProps> = ({ children }) => {
  const [currentAudio, setCurrentAudio] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const play = (audioSrc: string) => {
    // Stop current audio if playing
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }

    // Create new audio instance
    const audio = new Audio(audioSrc);
    audioRef.current = audio;
    setCurrentAudio(audioSrc);
    setIsPlaying(true);

    // Handle audio end
    audio.addEventListener('ended', () => {
      setIsPlaying(false);
      setCurrentAudio(null);
    });

    // Handle errors
    audio.addEventListener('error', () => {
      setIsPlaying(false);
      setCurrentAudio(null);
      console.error('Error playing audio:', audioSrc);
    });

    audio.play().catch(err => {
      setIsPlaying(false);
      setCurrentAudio(null);
      console.error('Failed to play audio:', err);
    });
  };

  const pause = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      setIsPlaying(false);
    }
  };

  const stop = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setIsPlaying(false);
    setCurrentAudio(null);
  };

  return (
    <AudioContext.Provider value={{ currentAudio, isPlaying, play, pause, stop }}>
      {children}
    </AudioContext.Provider>
  );
};

export const useAudio = (): AudioContextType => {
  const context = useContext(AudioContext);
  if (!context) {
    throw new Error('useAudio must be used within an AudioProvider');
  }
  return context;
};

type AudioButtonProps = {
  children: ReactNode;
  audioSrc: string;
} & React.ButtonHTMLAttributes<HTMLButtonElement>

// audio button
export const AudioButton = ({ 
  children,
  audioSrc, 
  ...props
}: AudioButtonProps) => {
  const { currentAudio, isPlaying, play, pause } = useAudio();
  
  const isThisAudioPlaying = currentAudio === audioSrc && isPlaying;

  const handleClick = () => {
    if (isThisAudioPlaying) {
      pause();
    } else {
      play(audioSrc);
    }
  };

  return (
    <button {...props} onClick={handleClick} >
      {children}
    </button>
  );
};